import React, { useState, useEffect } from 'react';
import { Button, Tooltip, Spin, Tabs, List, Empty, notification } from 'antd';
import {
  PlusOutlined,
  FolderOpenOutlined,
  FileTextOutlined,
  DeleteOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { getWorkflowsByUser, getWorkflowTemplates } from '../../API/services/workflowsService';
import { t } from '../../i18n/i18n';
import NodePanel from './NodePanel';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import { formatWorkflowDate } from '../../utils/sandbox/sandboxUtils';

const { TabPane } = Tabs;

const SidebarPanel = ({ sandbox, userId, collapsed, setCollapsed }) => {
  const [savedWorkflows, setSavedWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const loadSavedWorkflows = async () => {
    try {
      setLoadingWorkflows(true);
      const workflows = await getWorkflowsByUser(userId);
      setSavedWorkflows(workflows);
    } catch (err) {
      notification.error({
        message: t('ALKYMATION_ERROR_LOADING_SAVED_WORKFLOWS'),
        placement: 'topRight',
      });
    } finally {
      setLoadingWorkflows(false);
    }
  };

  const loadWorkflowTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const templates = await getWorkflowTemplates();
      setWorkflowTemplates(templates);
    } catch (err) {
      notification.error({
        message: t('ALKYMATION_ERROR_LOADING_TEMPLATES'),
        placement: 'topRight',
      });
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    if (userId) {
      loadSavedWorkflows();
      loadWorkflowTemplates();
    }
  }, [userId]);

  const handleLoadWorkflow = async (workflow) => {
    try {
      const workflowId = workflow._id || workflow.mongoId;
      await sandbox.loadWorkflow(workflowId);
      notification.success({
        message: `${t('ALKYMATION_WORKFLOW_LOADED')}: "${workflow.name}"`,
        placement: 'topRight',
      });
    } catch (err) {
      notification.error({
        message: t('ALKYMATION_ERROR_LOADING_WORKFLOW'),
        placement: 'topRight',
      });
    }
  };

  const handleDeleteWorkflow = (workflow) => {
    setWorkflowToDelete(workflow);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteWorkflow = async () => {
    if (!workflowToDelete) return;

    try {
      await sandbox.deleteWorkflowById(workflowToDelete._id);
      setSavedWorkflows(savedWorkflows.filter((w) => w._id !== workflowToDelete._id));
      notification.success({
        message: t('ALKYMATION_WORKFLOW_DELETED'),
        placement: 'topRight',
      });
    } catch (err) {
      console.error('Error al eliminar el flujo:', err);
      notification.error({
        message: t('ALKYMATION_ERROR_DELETING_WORKFLOW'),
        placement: 'topRight',
      });
    } finally {
      setWorkflowToDelete(null);
    }
  };

  const handleLoadTemplate = async (template) => {
    try {
      await sandbox.loadWorkflowFromTemplate(template);
      notification.success({
        message: `${t('ALKYMATION_TEMPLATE_LOADED')}: "${template.name}"`,
        placement: 'topRight',
      });
    } catch (err) {
      notification.error({
        message: t('ALKYMATION_ERROR_LOADING_TEMPLATE'),
        placement: 'topRight',
      });
    }
  };

  const WorkflowsTab = () => (
    <div className="saved-workflows">
      {loadingWorkflows ? (
        <div className="d-flex justify-content-center p-4">
          <Spin />
        </div>
      ) : savedWorkflows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('ALKYMATION_NO_SAVED_WORKFLOWS')}
        />
      ) : (
        <List
          size="small"
          dataSource={savedWorkflows}
          renderItem={(workflow) => (
            <List.Item
              className="workflow-item"
              actions={[
                <Tooltip key="load" title={t('ALKYMATION_LOAD_WORKFLOW')}>
                  <Button
                    type="text"
                    icon={<FolderOpenOutlined />}
                    size="small"
                    onClick={() => handleLoadWorkflow(workflow)}
                  />
                </Tooltip>,
                <Tooltip key="delete" title={t('ALKYMATION_DELETE')}>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    onClick={() => handleDeleteWorkflow(workflow)}
                  />
                </Tooltip>,
              ]}
            >
              <List.Item.Meta
                avatar={<FileTextOutlined style={{ fontSize: '20px', color: '#1890ff' }} />}
                title={workflow.name}
                description={
                  <div>
                    <small>{workflow.description || t('ALKYMATION_WITHOUT_DESCRIPTION')}</small>
                    <br />
                    <small className="text-muted">
                      {workflow.nodes?.length || 0} {t('ALKYMATION_NODE_COUNT')}
                      {workflow.updatedAt && <> • {formatWorkflowDate(workflow.updatedAt)}</>}
                    </small>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}

      <Button
        type="link"
        icon={<FolderOpenOutlined />}
        onClick={loadSavedWorkflows}
        className="d-flex align-items-center justify-content-center mt-2"
        block
      >
        {t('ALKYMATION_UPDATE_LIST')}
      </Button>
    </div>
  );

  const TemplatesTab = () => (
    <div className="saved-workflows">
      {loadingTemplates ? (
        <div className="d-flex justify-content-center p-4">
          <Spin />
        </div>
      ) : workflowTemplates.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('ALKYMATION_NO_TEMPLATES_AVAILABLE')}
        />
      ) : (
        <List
          size="small"
          dataSource={workflowTemplates}
          renderItem={(template) => (
            <List.Item
              className="workflow-item"
              actions={[
                <Tooltip key="load" title={t('ALKYMATION_LOAD_TEMPLATE')}>
                  <Button
                    type="text"
                    icon={<FolderOpenOutlined />}
                    size="small"
                    onClick={() => handleLoadTemplate(template)}
                  />
                </Tooltip>,
              ]}
            >
              <List.Item.Meta
                avatar={<FileTextOutlined style={{ fontSize: '20px', color: '#52c41a' }} />}
                title={template.name}
                description={
                  <div>
                    <small>{template.description || t('ALKYMATION_WITHOUT_DESCRIPTION')}</small>
                    <br />
                    <small className="text-muted">
                      {template.nodes?.length || 0} {t('ALKYMATION_NODE_COUNT')}
                    </small>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}

      <Button
        type="link"
        icon={<FolderOpenOutlined />}
        onClick={loadWorkflowTemplates}
        className="d-flex align-items-center justify-content-center mt-2"
        block
      >
        {t('ALKYMATION_UPDATE_LIST')}
      </Button>
    </div>
  );

  if (collapsed) {
    return (
      <div className="sidebar-collapsed">
        <div className="collapsed-header">
          <Tooltip title={t('ALKYMATION_EXPAND_SIDEBAR')} placement="right">
            <Button
              type="text"
              icon={<MenuUnfoldOutlined />}
              onClick={() => setCollapsed(false)}
              className="collapse-btn"
              size="large"
            />
          </Tooltip>
        </div>

        <div className="collapsed-spacer" />

        <div className="collapsed-footer">
          <div className="workflow-stats-mini">
            <FileTextOutlined style={{ fontSize: '18px', color: '#4987be' }} />
            <div className="stats-numbers">
              <span className="stats-count">{sandbox.workflow?.nodes?.length || 0}</span>
              <span className="stats-separator">•</span>
              <span className="stats-count">{sandbox.workflow?.connections?.length || 0}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sidebar-panel">
      <div className="panel-header">
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">{t('ALKYMATION_PANEL_TITLE')}</h4>
          <Tooltip title={t('ALKYMATION_COLLAPSE_SIDEBAR')} placement="left">
            <Button
              type="text"
              icon={<MenuFoldOutlined />}
              onClick={() => setCollapsed(true)}
              className="collapse-btn"
              size="large"
            />
          </Tooltip>
        </div>
      </div>

      <Tabs defaultActiveKey="nodes" className="sidebar-tabs">
        <TabPane
          tab={
            <span>
              <PlusOutlined />
              {t('ALKYMATION_NODES_TAB')}
            </span>
          }
          key="nodes"
        >
          <NodePanel sandbox={sandbox} hideFooter />
        </TabPane>

        <TabPane
          tab={
            <span>
              <FolderOpenOutlined />
              {t('ALKYMATION_WORKFLOWS_TAB')}
            </span>
          }
          key="workflows"
        >
          <div className="panel-content">
            <WorkflowsTab />
          </div>
        </TabPane>

        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              {t('ALKYMATION_TEMPLATES_TAB')}
            </span>
          }
          key="templates"
        >
          <div className="panel-content">
            <TemplatesTab />
          </div>
        </TabPane>
      </Tabs>

      <div className="panel-footer">
        <div className="workflow-info">
          <small>
            <strong>{t('ALKYMATION_CURRENT_WORKFLOW')}:</strong>
            <br />
            {sandbox.workflow?.name || t('ALKYMATION_WITHOUT_NAME')}
            <br />
            <span className="text-muted">
              {sandbox.workflow?.nodes?.length || 0} {t('ALKYMATION_NODE_COUNT')},{' '}
              {sandbox.workflow?.connections?.length || 0} {t('ALKYMATION_CONNECTION_COUNT')}
            </span>
          </small>
        </div>
      </div>

      {workflowToDelete && showDeleteConfirm && (
        <div
          className="confirm-modal-overlay position-fixed d-flex align-items-center justify-content-center"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="confirm-modal-wrapper position-static"
          >
            <ConfirmModal
              show={showDeleteConfirm}
              setShow={setShowDeleteConfirm}
              callback={confirmDeleteWorkflow}
              withoutDontAsk
              title={t('ALKYMATION_DELETE_WORKFLOW_MESSAGE')}
              text={workflowToDelete.name}
              cautionText={t('ALKYMATION_DELETE_WORKFLOW_WARNING')}
              confirmButtonText={t('ALKYMATION_DELETE')}
              cancelButtonText={t('ALKYMATION_CANCEL')}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SidebarPanel;
